/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 위젯 클래스를 정의한다.
 *
 * @file /src/elements/widget/Widget.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2025. 12. 12.
 */
import * as parent from '@base/Element';
import { Panel } from '@components/desktop/Panel';
import { Ajax } from '@utils/Ajax';
import { Html } from '@utils/Html';
import { Dragger } from '@utils/Dragger';
import { Dom } from '@utils/Dom';
import { UUID } from '@utils/UUID';
import './Widget.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 위젯 고유 ID
     */
    id?: string;

    /**
     * 위젯을 가져올 URL
     */
    url: string;

    /**
     * 그리드 column 수
     */
    column?: number;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Widget extends parent.Element {
    /**
     * 컴포넌트 타입
     */
    type = 'widget';

    /**
     * 위젯 고유 ID
     */
    id: string;

    /**
     * 위젯을 가져올 URL
     */
    url: string;

    /**
     * 그리드 column 수
     */
    column: number = 1;

    /**
     * 정렬 프리뷰 및 위치 계산용 정보
     * @property left - 요소 초기 left 위치
     * @property top - 요소 초기 top 위치
     * @property width - 요소 초기 width
     * @property height - 요소 초기 height
     * @property scroll - 스크롤 시작 위치
     * @property $preview - 드래그 중 표시되는 프리뷰 DOM
     */
    sorter: { left: number; top: number; width: number; height: number; scroll: number; $preview: Dom } = {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        scroll: 0,
        $preview: null,
    };

    /**
     * 위젯 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Widget> = null) {
        super(properties);

        this.id = this.properties.id ?? UUID.v4();
        this.url = this.properties.url;
        this.column = this.properties.column ?? 1;

        // properties에 id가 없으면 새로 생성한 id 설정
        if (!this.properties.id) {
            this.properties.id = this.id;
        }
    }

    /**
     * 엘리먼트 DOM 을 가져온다.
     *
     * @returns 엘리먼트 DOM
     */
    $getElement(): Dom {
        this.$element ??= super.$getElement();
        this.initializeDragAndDrop();

        return this.$element;
    }

    /**
     * 데스크톱 컴포넌트를 가져온다.
     *
     * @returns 데스크톱
     */
    getComponent(): Panel {
        return this.component as Panel;
    }

    /**
     * 드래그 앤 드롭 기능을 초기화한다.
     */
    private initializeDragAndDrop(): void {
        const drag = new Dragger(this.$element, {
            listeners: {
                start: (_, tracker) => this.handleDragStart(tracker),
                drag: (_, tracker) => this.handleDrag(tracker),
                end: (_, tracker) => this.handleDragEnd(tracker),
            },
        });

        this.$element.setData('drag', drag, false);
    }

    /**
     * 드래그 시작 시 호출되는 핸들러
     */
    private handleDragStart(tracker: Dragger): void {
        const $widgets = this.getComponent().$getWidgets();
        const position = this.$element.getPosition($widgets);

        this.sorter.left = position.left;
        this.sorter.top = position.top;
        this.sorter.width = this.$element.getOuterWidth();
        this.sorter.height = this.$element.getOuterHeight();
        this.sorter.scroll = this.getComponent().getScroller()?.getScrollPosition().y ?? 0;

        const scroller = this.getComponent().getScroller();
        scroller.addEvent('active', this.handleDrag.bind(this, tracker));
    }

    /**
     * 프리뷰 생성
     */
    private createDragPreview(): void {
        if (this.sorter.$preview !== null) return;

        this.sorter.$preview = this.$element
            .clone()
            .removeClass('dragging')
            .addClass('drag')
            .setStyle('left', `${this.sorter.left}px`)
            .setStyle('top', `${this.sorter.top}px`)
            .setStyle('width', `${this.sorter.width}px`)
            .setStyle('height', `${this.sorter.height}px`);

        this.getComponent().$getWidgets().append(this.sorter.$preview);
    }

    /**
     * 드래그 중일 때 호출되는 핸들러
     */
    private handleDrag(tracker: Dragger): void {
        this.createDragPreview();
        this.$element.addClass('dragging');
        this.updatePreviewPosition(tracker);
        this.autoScroll(tracker);
    }

    /**
     * 프리뷰 위치 업데이트
     */
    private updatePreviewPosition(tracker: Dragger): void {
        const { x, y } = tracker.getLength();
        const scroll = this.getComponent().getScroller()?.getScrollPosition().y ?? 0;
        const $widgets = this.getComponent().$getWidgets();
        const position = this.$element.getPosition($widgets);

        let translateX = x;
        let translateY = y - (this.sorter.scroll - scroll);

        // 경계 제한
        if (position.left + x < 0) {
            translateX = -position.left;
        }
        if (position.left + x + this.sorter.width > $widgets.getOuterWidth()) {
            translateX = $widgets.getOuterWidth() - this.sorter.width - position.left;
        }
        if (position.top + translateY < 0) {
            translateY = -position.top;
        }

        this.sorter.$preview.setStyle('transform', `translate(${translateX}px, ${translateY}px)`);
    }

    /**
     * 자동 스크롤 처리
     */
    private autoScroll(tracker: Dragger): void {
        const pointerY = tracker.getPosition().y;
        const compEl = this.getComponent().$getComponent();
        const top = compEl.getOffset().top;
        const bottom = top + compEl.getOuterHeight();
        const scroller = this.getComponent().getScroller();

        const scrollConfig = [
            { edge: 'top', thresholds: [20, 50, 80], speeds: [-3, -2, -1] },
            { edge: 'bottom', thresholds: [20, 50, 80], speeds: [3, 2, 1] },
        ];

        let speed = 0;
        for (const { edge, thresholds, speeds } of scrollConfig) {
            const distance = edge === 'top' ? pointerY - top : bottom - pointerY;

            for (let i = 0; i < thresholds.length; i++) {
                if (distance < thresholds[i]) {
                    speed = speeds[i];
                    break;
                }
            }
            if (speed !== 0) break;
        }

        if (speed !== 0) {
            scroller.startScroll('y', speed);
        } else {
            scroller.stopScroll('y');
        }
    }

    /**
     * 이동 시 이전 혹은 다음 Item을 가져온다.
     *
     * @param x - x축 이동값
     * @param y - y축 이동값
     * @returns 이동 시 이전 혹은 다음 Widget
     */
    private getDropPosition(x: number, y: number): Widget['properties'] | null {
        const $widgets = this.getComponent().$getWidgets();
        const gap = Number($widgets.getStyle('gap').replace('px', ''));

        const columns = this.getComponent().columns;
        const widthGap = ($widgets.getOuterWidth() + gap) / columns;
        const heightGap = this.getComponent().rowHeight + gap;

        // 현재 위젯의 위치 찾기
        let currentColumn: number = null;
        let currentRow: number = null;
        this.getComponent().grid.forEach((row, rowIndex) => {
            let column = 0;
            row.forEach((item) => {
                if (item === this.properties) {
                    currentColumn = column;
                    currentRow = rowIndex;
                }

                column += item.column ?? 1;
            });
        });

        // 드래그한 픽셀 값을 그리드 좌표로 변환
        let targetColumn = currentColumn;
        let targetRow = currentRow;

        // x축 이동 처리 (좌우 이동)
        if (Math.abs(x) > widthGap / 2) {
            const positionColumn = Math.round(x / widthGap);
            targetColumn = currentColumn + positionColumn;
        }

        // y축 이동 처리 (상하 이동)
        if (Math.abs(y) > heightGap / 2) {
            const positionRow = Math.round(y / heightGap);
            targetRow = currentRow + positionRow;
        }

        // 움직이지 않음(이동 안함)
        if (targetColumn === currentColumn && targetRow === currentRow) {
            return null;
        }

        const row = this.getComponent().grid.find((_, rowIndex) => rowIndex === targetRow);
        if (!row) {
            return null;
        }

        let target = null;
        let column = 0;
        row.forEach((item) => {
            const start = column;
            const end = column + (item.column ?? 1) - 1;

            if (targetColumn >= start && targetColumn <= end) {
                target = item;
            }

            column += item.column ?? 1;
        });

        // 삽입 위치 결정
        if (targetRow < currentRow || (targetRow === currentRow && targetColumn < currentColumn)) {
            return target;
        } else {
            const widgets = this.getComponent().widgets;
            return widgets[widgets.findIndex((item) => item === target) + 1];
        }
    }

    /**
     * 드래그 종료 시 호출되는 핸들러
     */
    private async handleDragEnd(tracker: Dragger): Promise<void> {
        const { x, y } = tracker.getLength();
        const scroll = this.getComponent().getScroller()?.getScrollPosition().y ?? 0;
        const $widgets = this.getComponent().$getWidgets();
        const position = this.$element.getPosition($widgets);

        let positionX = x;
        let positionY = y - (this.sorter.scroll - scroll);

        // 경계 제한
        if (position.left + x < 0) positionX = -position.left;
        if (position.left + x + this.sorter.width > $widgets.getOuterWidth()) {
            positionX = $widgets.getOuterWidth() - this.sorter.width - position.left;
        }
        if (position.top + positionY < 0) positionY = -position.top;

        const targetWidget = this.getDropPosition(positionX, positionY);
        this.moveWidget(targetWidget);
        this.animatePreviewBack($widgets);
        this.cleanupDrag();
    }

    /**
     * 위젯 이동 처리
     */
    private moveWidget(targetWidget: Widget['properties'] | null): void {
        if (targetWidget === null) return;

        const widgets = this.getComponent().widgets;
        const from = widgets.findIndex((item) => item === this.properties);
        if (from === -1) return;

        const [item] = widgets.splice(from, 1);
        const to = widgets.findIndex((item) => item === targetWidget);

        if (to === -1) {
            widgets.push(item);
        } else {
            widgets.splice(to, 0, item);
        }

        this.getComponent().reorderWidgets();
    }

    /**
     * 프리뷰 애니메이션 및 정리
     */
    private animatePreviewBack($widgets: Dom): void {
        const currentPosition = this.$element.getPosition($widgets);
        const rollbackX = currentPosition.left - this.sorter.left;
        const rollbackY = currentPosition.top - this.sorter.top;

        this.sorter.$preview.animate(
            { transform: `translate(${rollbackX}px, ${rollbackY}px)` },
            { duration: 200, easing: 'ease-in-out' },
            () => {
                this.sorter.$preview.remove();
                this.resetSorter();
                this.$element.removeClass('dragging');
            }
        );
    }

    /**
     * 드래그 정리
     */
    private cleanupDrag(): void {
        const scroller = this.getComponent().getScroller();
        scroller.stopScroll('y');
        scroller.removeEvent('active');
        this.$element.removeClass('drag');
    }

    /**
     * sorter 상태 초기화
     */
    private resetSorter(): void {
        this.sorter.left = 0;
        this.sorter.top = 0;
        this.sorter.width = 0;
        this.sorter.height = 0;
        this.sorter.scroll = 0;
        this.sorter.$preview = null;
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        const $span = Html.create('span');

        const results = await Ajax.get(this.url);
        if (results.success) {
            $span.html(results.data.content);
        }
        this.$getElement().append($span);
        this.$getElement().setStyle('grid-column', `auto / span ${this.column}`);

        await this.onRender();
    }
}
